import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Button, Card, CardBody } from '@platform/ui-kit'
import { reportBoundaryError } from '@/lib/observability'

/* ------------------------------------------------------------------
   Error Boundary — سند مادر بند ۵.۲ و اصل P-06 (Fail Isolation)
   ------------------------------------------------------------------
   «خرابی Render یک ماژول با Error Boundary از کل پنل جدا می‌شود.»

   یعنی اگر ماژول مرخصی خطا بدهد، Shell و بقیه ماژول‌ها باید سالم
   بمانند و کاربر بتواند به کارش ادامه دهد.
   ------------------------------------------------------------------ */

interface Props {
  children: ReactNode
  /** نام ماژول برای نمایش و لاگ */
  moduleName?: string
  /** با تغییر این کلید، Boundary خودش را ریست می‌کند (مثلاً تغییر مسیر) */
  resetKey?: string
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prevProps: Props): void {
    // تغییر مسیر باید خطای قبلی را پاک کند، وگرنه کاربر گیر می‌کند
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // خطای Render هرگز به Backend نمی‌رسد، پس اگر اینجا گزارش نشود هیچ
    // ردی از آن نمی‌ماند — کاربر فقط کارت خطا را می‌بیند و می‌رود.
    reportBoundaryError(error, info.componentStack, this.props.moduleName)

    console.error(
      `[ErrorBoundary]${this.props.moduleName ? ` ماژول «${this.props.moduleName}»` : ''}`,
      error,
      info.componentStack,
    )
  }

  reset = (): void => this.setState({ error: null })

  /**
   * ==================================================================
   * 🔴 شکستِ بارگذاریِ یک صفحهٔ Lazy با `reset` درست نمی‌شود.
   *
   * ⚠️ مرورگر نتیجهٔ شکست‌خوردهٔ `import()` را برای همان نشانی نگه
   * می‌دارد. پس `setState` صفحه را دوباره رندر می‌کند، همان ماژول
   * دوباره خواسته می‌شود، و همان خطا برمی‌گردد — دکمهٔ «تلاش دوباره»
   * بی‌اثر به نظر می‌رسد در حالی که دارد کارش را می‌کند.
   *
   * 🔴 تنها راهِ بیرون‌آمدن، بارگذاریِ دوبارهٔ خودِ سند است.
   * ==================================================================
   */
  private isChunkLoadError(error: Error): boolean {
    return /dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
      `${error.name} ${error.message}`,
    )
  }

  private retry = (): void => {
    const { error } = this.state

    if (error && this.isChunkLoadError(error)) {
      window.location.reload()

      return
    }

    this.reset()
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardBody className="flex flex-col items-center gap-3 py-10 text-center">
            <img
              src="/icons/3d/error.png"
              alt=""
              aria-hidden="true"
              width={88}
              height={88}
              decoding="async"
              className="size-22 object-contain"
            />

            <h2 className="text-[length:var(--pf-text-lg)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
              {this.props.moduleName
                ? `بخش «${this.props.moduleName}» بارگذاری نشد`
                : 'خطای غیرمنتظره'}
            </h2>

            <p className="max-w-sm text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
              بقیه بخش‌های سامانه سالم هستند و می‌توانید کارتان را ادامه دهید.
              اگر مشکل تکرار شد، به پشتیبانی اطلاع دهید.
            </p>

            {import.meta.env.DEV && (
              <pre
                dir="ltr"
                className="mt-1 max-h-40 w-full overflow-auto rounded-[var(--pf-radius)] bg-[var(--pf-surface-muted)] p-3 text-start text-[length:var(--pf-text-2xs)] text-[var(--pf-text-muted)]"
              >
                {error.message}
                {'\n'}
                {error.stack}
              </pre>
            )}

            <div className="mt-1 flex gap-2">
              <Button variant="primary" size="sm" onClick={this.retry}>
                تلاش دوباره
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.location.assign('/app')}>
                بازگشت به داشبورد
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    )
  }
}
